// Who you are, as far as the archive is concerned. Two things live here, and they answer two
// different questions.
//
// The SESSION TOKEN is the account: the server handed it out in exchange for a name and a
// password, it can revoke it, and while it is here every table shows the name of that account
// instead of whatever the form says.
//
// The DEVICE TOKEN came first and stays for whoever never registers. The device makes it once and
// keeps it; whoever holds it IS that player, which is why it doubles as the code you paste on a
// second device. It is also what a fresh account takes over on registration, so a history started
// as a guest is not lost the day it gets a name.

const StorageKey = 'marz.accountToken';
const SessionKey = 'marz.sessionToken';

// A token is stored and read as an opaque string; this only rejects the shapes that could not have
// come from here, so a mistyped recovery code fails at once instead of silently starting a new
// history under an empty name
const TokenPattern = /^[0-9a-f-]{16,64}$/i;

export function isValidToken(token) {
  return typeof token === 'string' && TokenPattern.test(token.trim());
}

// The current token, created on first use. Storage can be blocked (private browsing, or a browser
// set to refuse site data): the game then still plays, with a token that lives as long as the tab
let inMemoryToken = null;

export function accountToken() {
  if (inMemoryToken) return inMemoryToken;
  try {
    const stored = localStorage.getItem(StorageKey);
    if (isValidToken(stored)) {
      inMemoryToken = stored;
      return inMemoryToken;
    }
  } catch {
    // Falls through to a fresh token below
  }
  inMemoryToken = generateToken();
  persist(inMemoryToken);
  return inMemoryToken;
}

// Adopting the code from another device: from here on this browser IS that player
export function setAccountToken(token) {
  const clean = String(token).trim().toLowerCase();
  if (!isValidToken(clean)) return false;
  inMemoryToken = clean;
  persist(clean);
  return true;
}

function persist(token) {
  try {
    localStorage.setItem(StorageKey, token);
  } catch {
    // Not being able to remember it is not worth interrupting anyone over: see above
  }
}

// The account, when there is one. Kept apart from the device token above: signing out ends this
// one and leaves the other alone, which is what makes "gioca come ospite" go back to being you
let inMemorySession = null;

export function sessionToken() {
  if (inMemorySession !== null) return inMemorySession;
  try {
    inMemorySession = localStorage.getItem(SessionKey) ?? '';
  } catch {
    inMemorySession = '';
  }
  return inMemorySession;
}

export function setSessionToken(token) {
  inMemorySession = typeof token === 'string' ? token : '';
  try {
    if (inMemorySession) localStorage.setItem(SessionKey, inMemorySession);
    else localStorage.removeItem(SessionKey);
  } catch {
    // A session that cannot be written down lasts as long as the tab, which still plays
  }
}

// crypto.randomUUID exists only in a secure context, and playing over plain http on the home
// network is a supported way to use this, so the random bytes are formatted here instead
function generateToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
